import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Medal, Share2, Camera, Loader2, Sparkles, MapPin, Plane, TrendingUp, Users, Globe, IndianRupee, ArrowRight, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fileUrl } from "../../lib/api";
import { LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const PERIODS = [["today", "Today"], ["week", "This Week"], ["month", "Last 30 Days"], ["all", "All Time"]];

const CSS = `
@keyframes lb-grad { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes lb-float { 0%,100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-12px) rotate(4deg); } }
@keyframes lb-confetti { 0% { transform: translateY(-8%) rotate(0); opacity: 0; } 8% { opacity: 1; } 100% { transform: translateY(105vh) rotate(540deg); opacity: 0; } }
@keyframes lb-sparkle { 0%,100% { transform: scale(.7); opacity: .5; } 50% { transform: scale(1.15); opacity: 1; } }
@keyframes lb-glowgold { 0%,100% { box-shadow: 0 0 26px rgba(245,183,0,.35), 0 18px 40px -18px rgba(245,183,0,.45); } 50% { box-shadow: 0 0 52px rgba(245,183,0,.6), 0 18px 40px -18px rgba(245,183,0,.6); } }
@keyframes lb-shine { 0% { transform: translateX(-120%) skewX(-18deg); } 100% { transform: translateX(240%) skewX(-18deg); } }
.lb-hero-grad { background: linear-gradient(115deg, #007A4D 0%, #065f46 22%, #2563EB 52%, #06B6D4 76%, #7C3AED 100%); background-size: 220% 220%; animation: lb-grad 16s ease infinite; }
.lb-gold-text { background: linear-gradient(100deg, #FFF3A3, #FFD700 40%, #F5B700 65%, #FFF3A3); -webkit-background-clip: text; background-clip: text; color: transparent; }
.lb-card { border-radius: 22px; background: rgba(255,255,255,.85); backdrop-filter: blur(10px); border: 1px solid rgba(15,23,42,.07); box-shadow: 0 10px 30px -14px rgba(15,23,42,.14); transition: transform .25s ease, box-shadow .25s ease; }
.lb-card:hover { transform: translateY(-3px); box-shadow: 0 18px 40px -16px rgba(15,23,42,.22); }
.lb-gold-frame { border: 2.5px solid #F5B700; animation: lb-glowgold 3s ease-in-out infinite; }
.lb-shine { position: relative; overflow: hidden; }
.lb-shine::after { content: ""; position: absolute; top: 0; bottom: 0; width: 45%; background: linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent); animation: lb-shine 4.5s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .lb-hero-grad, .lb-gold-frame, .lb-shine::after, .lb-anim { animation: none !important; } }
`;

const GRAD = {
  today: "linear-gradient(135deg, #00A86B, #06B6D4)",
  week: "linear-gradient(135deg, #2563EB, #1e40af)",
  month: "linear-gradient(135deg, #7C3AED, #DB2777)",
  all: "linear-gradient(135deg, #F97316, #F5B700)",
};
const GRAD_ICON = { today: IndianRupee, week: TrendingUp, month: Sparkles, all: Trophy };

function useCountUp(target, dur = 900) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !target) { setV(target || 0); return; }
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [target, dur]);
  return v;
}

function Avatar({ photo, name, cls = "h-12 w-12 text-base", ring = "" }) {
  if (photo) return <img src={fileUrl(photo)} alt={name} className={`${cls} ${ring} rounded-full object-cover shrink-0`} />;
  const ini = (name || "P").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className={`${cls} ${ring} rounded-full flex items-center justify-center font-display font-extrabold text-white shrink-0`} style={{ background: "linear-gradient(135deg, #007A4D, #2563EB)" }}>{ini}</div>;
}

function Amount({ n, className = "" }) {
  const v = useCountUp(n);
  return <span className={className}>{fmtINR(v)}</span>;
}

const PODIUM = {
  1: { frame: "lb-gold-frame", badgeBg: "#F5B700", badgeTx: "#3f2d00", label: "TOP EARNER", icon: Crown, base: "linear-gradient(180deg,#F5B700,#b8860b)", h: "h-20 sm:h-24", ring: "ring-4 ring-yellow-300" },
  2: { frame: "border-[2.5px] border-[#AEB7C4]", badgeBg: "#D9DEE5", badgeTx: "#334155", label: "RISING STAR", icon: Medal, base: "linear-gradient(180deg,#D9DEE5,#94a3b8)", h: "h-14 sm:h-16", ring: "ring-4 ring-slate-200" },
  3: { frame: "border-[2.5px] border-[#CD7F32]", badgeBg: "#CD7F32", badgeTx: "#FFF0E0", label: "TOP PERFORMER", icon: Medal, base: "linear-gradient(180deg,#E3A66B,#8b5423)", h: "h-10 sm:h-12", ring: "ring-4 ring-orange-200" },
};

function PodiumCol({ row, place, onView }) {
  const s = PODIUM[place];
  const big = place === 1;
  return (
    <div data-testid={`podium-${place}`} className={`flex flex-col items-center ${big ? "sm:-mt-8 z-10" : ""}`}>
      {big && <Crown className="h-9 w-9 mb-1 lb-anim" style={{ color: "#F5B700", filter: "drop-shadow(0 2px 8px rgba(245,183,0,.7))", animation: "lb-sparkle 2.4s ease-in-out infinite" }} />}
      <button onClick={() => onView(row)} className={`lb-card ${s.frame} ${big ? "lb-shine p-6 sm:p-7" : "p-5"} text-center w-full cursor-pointer`}>
        <div className="flex justify-center">
          <Avatar photo={row.photo} name={row.name} cls={big ? "h-24 w-24 text-3xl" : "h-16 w-16 text-xl"} ring={`${s.ring} shadow-xl`} />
        </div>
        <p className={`font-display font-extrabold tracking-tight text-zinc-900 mt-3 ${big ? "text-xl" : "text-base"}`}>{row.name}</p>
        {row.specialization && <p className="text-[11px] text-zinc-500 flex items-center justify-center gap-1 mt-0.5"><Sparkles className="h-3 w-3" />{row.specialization}</p>}
        <Amount n={row.total} className={`font-display font-extrabold block mt-1 ${big ? "text-3xl" : "text-xl"}`} />
        <p className="text-[11px] text-zinc-500 mt-1">{row.websites} websites{row.crm_earnings ? ` · ${fmtINR(row.crm_earnings)} CRM` : ""}</p>
        <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-extrabold" style={{ background: s.badgeBg, color: s.badgeTx }}>
            <s.icon className="h-3 w-3" /> #{place}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-extrabold tracking-wider" style={{ background: `${s.badgeBg}33`, color: big ? "#b8860b" : "#475569" }}>{s.label}</span>
        </div>
      </button>
      <div className={`w-4/5 rounded-t-2xl ${s.h}`} style={{ background: s.base }} />
    </div>
  );
}

export default function PartnerLeaderboard() {
  const [period, setPeriod] = useState("today");
  const [cache, setCache] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [profile, setProfile] = useState({ display_name: "", bio: "", city: "", specialization: "", photo: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all(PERIODS.map(([k]) => api.get(`/partner/leaderboard?period=${k}`).then((r) => [k, r.data]).catch(() => [k, null])))
      .then((entries) => setCache(Object.fromEntries(entries)));
    api.get("/partner/freelancer-profile").then((r) => setProfile({ display_name: "", bio: "", city: "", specialization: "", photo: "", ...r.data.profile })).catch(() => {});
  }, []);

  const data = cache[period];
  const top = data?.top || [];
  const top3 = top.slice(0, 3);
  const rest = top.slice(3);
  const insights = useMemo(() => {
    if (!top.length) return null;
    const tot = top.reduce((a, r) => a + r.total, 0);
    return { total: tot, websites: top.reduce((a, r) => a + r.websites, 0), crm: top.reduce((a, r) => a + r.crm_earnings, 0), active: top.filter((r) => r.total > 0).length, avg: Math.round(tot / top.length) };
  }, [top]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/partner/freelancer-profile", profile);
      toast.success("Freelancer profile saved");
      setEditOpen(false);
      setCache({});
      PERIODS.forEach(([k]) => api.get(`/partner/leaderboard?period=${k}`).then((r) => setCache((c) => ({ ...c, [k]: r.data }))));
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setProfile((p) => ({ ...p, photo: data.url }));
      toast.success("Photo uploaded");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploading(false); }
  };

  const share = async (row) => {
    const text = `🏆 I'm ranked #${row.rank} on MILTROO Top Freelancers with ${fmtINR(row.total)} verified earnings! Start. Sell. Grow. Anywhere.`;
    if (navigator.share) { try { await navigator.share({ title: "MILTROO Achievement", text }); return; } catch {} }
    try { await navigator.clipboard.writeText(text); toast.success("Achievement copied — paste it anywhere!"); } catch { toast.error("Share not supported"); }
  };

  return (
    <div data-testid="leaderboard-page" className="space-y-8">
      <style>{CSS}</style>

      {/* HERO */}
      <section className="lb-hero-grad relative overflow-hidden rounded-[28px] text-white">
        <div className="absolute -top-16 -left-16 h-56 w-56 rounded-full opacity-25" style={{ background: "radial-gradient(circle, #06B6D4, transparent 70%)" }} />
        <div className="absolute -bottom-20 -right-10 h-64 w-64 rounded-full opacity-25" style={{ background: "radial-gradient(circle, #F5B700, transparent 70%)" }} />
        {["#FFD700", "#06B6D4", "#A78BFA", "#34D399", "#FB7185"].flatMap((c, ci) =>
          [10, 26, 44, 62, 80, 93].map((l, i) => (
            <span key={`${ci}-${i}`} className="lb-confetti absolute rounded-sm" style={{ background: c, left: `${l}%`, width: 7, height: 12, animationDelay: `${(ci * 6 + i) * 0.4}s` }} />
          )))}
        <Trophy className="lb-anim absolute right-6 top-6 h-14 w-14 text-yellow-300/80 hidden sm:block" style={{ animation: "lb-float 5s ease-in-out infinite" }} />
        <Crown className="lb-anim absolute right-24 bottom-8 h-9 w-9 text-white/25 hidden sm:block" style={{ animation: "lb-float 6s ease-in-out infinite 1s" }} />
        <Plane className="lb-anim absolute left-8 bottom-6 h-9 w-9 text-white/25 hidden sm:block" style={{ animation: "lb-float 7s ease-in-out infinite .5s" }} />
        <div className="relative px-6 py-10 sm:px-12 sm:py-14 max-w-3xl">
          <p className="text-[11px] font-extrabold tracking-[0.25em] text-emerald-100/80 uppercase flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> MILTROO Freelancer Champions</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[1.05]">
            Top Freelancers <span className="lb-gold-text">Earnings</span>
          </h1>
          <p className="mt-3 text-base sm:text-lg font-semibold text-white/90">Real People. Real Earnings. Real Success.</p>
          <p className="mt-1 text-sm text-white/70 max-w-xl">Build websites. Sell CRM. Help businesses grow. Earn more with MILTROO.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/partner/challenges" data-testid="hero-challenges-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-emerald-800 px-6 h-11 text-sm font-extrabold shadow-lg hover:scale-[1.03] transition-transform">
              <Trophy className="h-4 w-4" /> Earning Challenges <ArrowRight className="h-4 w-4" />
            </Link>
            <button data-testid="edit-freelancer-profile" onClick={() => setEditOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 px-6 h-11 text-sm font-bold hover:bg-white/25 transition-colors">
              <Camera className="h-4 w-4" /> My Freelancer Profile
            </button>
          </div>
        </div>
      </section>

      {/* PERIOD TABS */}
      <div className="flex gap-2.5 flex-wrap" data-testid="period-tabs">
        {PERIODS.map(([k, l]) => (
          <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod(k)}
            className={`px-6 py-3 rounded-full text-sm font-extrabold transition-all duration-300 ${period === k ? "text-white shadow-lg scale-[1.03]" : "bg-white text-zinc-700 border border-zinc-200 hover:border-emerald-400 hover:text-emerald-700"}`}
            style={period === k ? { background: "linear-gradient(120deg, #007A4D, #2563EB)", boxShadow: "0 8px 24px -8px rgba(0,122,77,.5)" } : {}}>
            {l}
          </button>
        ))}
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="summary-cards">
        {PERIODS.map(([k, l]) => {
          const me = cache[k]?.me;
          const Ic = GRAD_ICON[k];
          return (
            <div key={k} data-testid={`summary-${k}`} className="lb-card overflow-hidden">
              <div className="h-1.5" style={{ background: GRAD[k] }} />
              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-widest text-zinc-500">{l === "Today" ? "Today's Earnings" : l}</p>
                  <span className="h-8 w-8 rounded-xl flex items-center justify-center text-white" style={{ background: GRAD[k] }}><Ic className="h-4 w-4" /></span>
                </div>
                <Amount n={me?.total || 0} className="font-display font-extrabold text-2xl sm:text-3xl text-zinc-900 block mt-1" />
                <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-emerald-600" /> {me?.websites || 0} websites{me?.crm_earnings ? ` · ${fmtINR(me.crm_earnings)} CRM` : ""}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {!data ? <div className="lb-card p-8"><LoadingRows rows={4} /></div> : (
        <div className="grid xl:grid-cols-3 gap-8 items-start">
          <div className="xl:col-span-2 space-y-8">
            {/* TOP 3 PODIUM */}
            {top3.length > 0 && (
              <section className="relative">
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-center text-zinc-900 dark:text-white mb-6 flex items-center justify-center gap-2">
                  <Crown className="h-6 w-6" style={{ color: "#F5B700" }} /> TOP 3 FREELANCERS
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-4 items-end max-w-3xl mx-auto">
                  {top3[1] ? <div className="order-2 sm:order-1"><PodiumCol row={top3[1]} place={2} onView={setViewRow} /></div> : <div className="hidden sm:block order-1" />}
                  {top3[0] && <div className="order-1 sm:order-2"><PodiumCol row={top3[0]} place={1} onView={setViewRow} /></div>}
                  {top3[2] ? <div className="order-3"><PodiumCol row={top3[2]} place={3} onView={setViewRow} /></div> : <div className="hidden sm:block order-3" />}
                </div>
              </section>
            )}

            {/* RANKS 4-10 */}
            {rest.length > 0 && (
              <section data-testid="leaderboard-rest" className="space-y-3">
                {rest.map((r) => (
                  <div key={r.partner_id} data-testid={`lb-row-${r.rank}`} className="lb-card p-4 flex items-center gap-4">
                    <span className="w-9 text-center font-display font-extrabold text-lg" style={{ color: "#007A4D" }}>#{r.rank}</span>
                    <Avatar photo={r.photo} name={r.name} cls="h-11 w-11 text-sm" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm text-zinc-900 truncate">{r.name}</p>
                      <p className="text-[11px] text-zinc-500">{r.websites} websites{r.crm_earnings ? ` · ${fmtINR(r.crm_earnings)} CRM` : ""}</p>
                    </div>
                    <Amount n={r.total} className="font-display font-extrabold text-lg text-zinc-900" />
                    <button data-testid={`view-profile-${r.rank}`} onClick={() => setViewRow(r)}
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3.5 h-8 text-[11px] font-bold text-zinc-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors">
                      <Eye className="h-3 w-3" /> View
                    </button>
                  </div>
                ))}
              </section>
            )}

            {top.length === 0 && (
              <div className="lb-card p-10 text-center">
                <Trophy className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
                <p className="font-display font-bold text-lg text-zinc-900">No earnings in this period yet</p>
                <p className="text-sm text-zinc-500 mt-1">Complete websites and earn CRM commissions to appear on the leaderboard.</p>
              </div>
            )}

            {/* MY RANK */}
            {data.me && (
              <div data-testid="my-rank-card" className="lb-card overflow-hidden">
                <div className="h-1.5" style={{ background: "linear-gradient(90deg, #007A4D, #2563EB, #F5B700)" }} />
                <div className="p-5 flex flex-wrap items-center gap-4">
                  <span className="font-display font-extrabold text-3xl" style={{ color: "#007A4D" }}>#{data.me.rank}</span>
                  <Avatar photo={data.me.photo} name={data.me.name} cls="h-12 w-12 text-base" />
                  <div className="flex-1 min-w-40">
                    <p className="font-bold text-zinc-900">You</p>
                    <p className="text-xs text-zinc-500">{data.me.websites} websites · {fmtINR(data.me.website_earnings)} website · {fmtINR(data.me.crm_earnings)} CRM</p>
                  </div>
                  <Amount n={data.me.total} className="font-display font-extrabold text-2xl text-zinc-900" />
                  <Button data-testid="share-achievement" onClick={() => share(data.me)} className="rounded-full font-bold text-white" style={{ background: "linear-gradient(120deg, #007A4D, #2563EB)" }}>
                    <Share2 className="h-4 w-4 mr-2" /> Share Achievement
                  </Button>
                </div>
              </div>
            )}

            {/* TRAVEL VISUAL */}
            <section className="relative overflow-hidden rounded-[28px] text-white" style={{ background: "linear-gradient(115deg, #0e7490, #2563EB 55%, #7C3AED)" }}>
              <Plane className="lb-anim absolute right-8 top-6 h-12 w-12 text-white/25" style={{ animation: "lb-float 6s ease-in-out infinite" }} />
              <Trophy className="lb-anim absolute right-24 bottom-6 h-8 w-8 text-yellow-300/50 hidden sm:block" style={{ animation: "lb-float 5s ease-in-out infinite 1s" }} />
              <div className="relative px-6 py-9 sm:px-10 max-w-xl">
                <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-cyan-100/80">MILTROO Rewards</p>
                <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-2">Earn Today. Explore Tomorrow.</h3>
                <p className="text-sm text-white/75 mt-2">Your hard work can unlock bigger rewards — travel, gifts and gold medals through Earning Challenges.</p>
              </div>
            </section>
          </div>

          {/* RIGHT INSIGHTS */}
          <aside className="space-y-5">
            <div className="lb-card p-5">
              <p className="font-display font-extrabold tracking-tight text-zinc-900 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4" style={{ color: "#007A4D" }} /> QUICK INSIGHTS</p>
              <div className="grid grid-cols-2 gap-3">
                {insights && [
                  { l: "Total Earnings", v: fmtINR(insights.total), g: GRAD.today, i: IndianRupee },
                  { l: "Websites Created", v: insights.websites, g: GRAD.week, i: Globe },
                  { l: "CRM Earnings", v: fmtINR(insights.crm), g: GRAD.month, i: Sparkles },
                  { l: "Active Freelancers", v: insights.active, g: GRAD.all, i: Users },
                ].map((c) => (
                  <div key={c.l} className="rounded-2xl p-3.5 text-white" style={{ background: c.g }}>
                    <c.i className="h-4 w-4 opacity-80" />
                    <p className="font-display font-extrabold text-lg mt-1">{c.v}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">{c.l}</p>
                  </div>
                ))}
                <div className="rounded-2xl p-3.5 border border-zinc-200 col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Average Earnings</p>
                  <p className="font-display font-extrabold text-xl text-zinc-900">{fmtINR(insights?.avg || 0)}</p>
                </div>
              </div>
            </div>
            <div className="rounded-[22px] p-6 text-white text-center" style={{ background: "linear-gradient(135deg, #007A4D, #00A86B)" }}>
              <Trophy className="lb-anim h-10 w-10 mx-auto text-yellow-300" style={{ animation: "lb-float 4s ease-in-out infinite" }} />
              <p className="font-display font-extrabold text-xl tracking-tight mt-3">Be the Next Top Earning Freelancer!</p>
              <p className="text-xs text-emerald-100/80 mt-1">Every website you ship moves you up the board.</p>
              <Link to="/partner/websites/new" data-testid="start-building-cta" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-emerald-800 px-6 h-10 text-sm font-extrabold hover:scale-[1.03] transition-transform">
                Start Building Today <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <Link to="/partner/challenges" data-testid="challenges-banner" className="block rounded-[22px] p-6 text-white relative overflow-hidden group" style={{ background: "linear-gradient(120deg, #2563EB, #007A4D 60%, #F5B700 130%)" }}>
              <Medal className="absolute -right-4 -bottom-4 h-24 w-24 text-white/10 group-hover:scale-110 transition-transform" />
              <p className="font-display font-extrabold text-xl tracking-tight flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-300" /> Earning Challenges</p>
              <p className="text-xs text-white/80 mt-1.5">Complete Earning Targets. Unlock Amazing Rewards.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-extrabold text-yellow-200">View Earning Challenges <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </aside>
        </div>
      )}

      {/* VIEW PROFILE MODAL */}
      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setViewRow(null)}>
          <div data-testid="view-profile-modal" className="lb-card p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center"><Avatar photo={viewRow.photo} name={viewRow.name} cls="h-20 w-20 text-2xl" ring="ring-4 ring-emerald-100" /></div>
            <p className="font-display font-extrabold text-xl text-zinc-900 mt-3">{viewRow.name}</p>
            {viewRow.specialization && <p className="text-xs text-zinc-500 mt-0.5">{viewRow.specialization}</p>}
            {viewRow.city && <p className="text-xs text-zinc-500 flex items-center justify-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {viewRow.city}</p>}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="rounded-xl bg-emerald-50 p-3"><p className="font-display font-extrabold text-emerald-700">#{viewRow.rank}</p><p className="text-[10px] font-bold text-zinc-500 uppercase">Rank</p></div>
              <div className="rounded-xl bg-blue-50 p-3"><p className="font-display font-extrabold text-blue-700">{viewRow.websites}</p><p className="text-[10px] font-bold text-zinc-500 uppercase">Websites</p></div>
              <div className="rounded-xl bg-amber-50 p-3"><p className="font-display font-extrabold text-amber-700">{fmtINR(viewRow.total)}</p><p className="text-[10px] font-bold text-zinc-500 uppercase">Earned</p></div>
            </div>
            <Button data-testid="view-profile-close" onClick={() => setViewRow(null)} className="rounded-full font-bold mt-5 w-full">Close</Button>
          </div>
        </div>
      )}

      {/* EDIT PROFILE MODAL */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setEditOpen(false)}>
          <form data-testid="freelancer-profile-modal" onSubmit={saveProfile} className="lb-card p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-extrabold text-lg text-zinc-900">My Freelancer Profile</h3>
            <div className="flex items-center gap-4">
              <Avatar photo={profile.photo} name={profile.display_name || "P"} cls="h-16 w-16 text-xl" />
              <label data-testid="photo-upload-label" className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-zinc-200 px-4 h-10 text-xs font-bold text-zinc-600 hover:border-emerald-400">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Change Photo
                <input data-testid="photo-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              </label>
            </div>
            {[["display_name", "Public Display Name"], ["city", "City / Location"], ["specialization", "Specialization (e.g. E-commerce Websites)"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-500">{l}</Label>
                <Input data-testid={`profile-${k}`} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="h-11 rounded-xl bg-white" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-500">Short Bio</Label>
              <textarea data-testid="profile-bio" value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm min-h-20" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" data-testid="profile-cancel" onClick={() => setEditOpen(false)} className="rounded-full font-bold">Cancel</Button>
              <Button type="submit" data-testid="profile-save" disabled={busy} className="rounded-full font-bold text-white" style={{ background: "linear-gradient(120deg, #007A4D, #2563EB)" }}>{busy ? "Saving..." : "Save Profile"}</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
